import { useState } from 'react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/shadcn/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/shadcn/tooltip'
import { useTheme } from '@/context/themeContext'
import { cn } from '@/lib/utils'

interface Option {
  label: string
  content: React.ReactNode
}

interface Props {
  trigger: React.ReactNode
  options: Option[]
  hoverContent: string
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  offset?: number
}

export function PopoverMenu({
  trigger,
  options,
  hoverContent,
  side = 'bottom',
  align = 'start',
  offset = 15,
}: Props) {
  const { theme } = useTheme()
  const [isHidden, setIsHidden] = useState(false)

  const handleHover = () => {
    const timer = setTimeout(() => {
      setIsHidden(false)
    }, 500)

    return () => clearTimeout(timer)
  }
  const isStringTrigger = typeof trigger === 'string'

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild onMouseEnter={handleHover}>
          <PopoverTrigger
            className={`text-foreground hover:bg-hover data-[state=open]:bg-hover flex h-9 cursor-pointer items-center justify-center rounded-lg text-sm ${isStringTrigger ? 'min-w-16 px-3' : 'w-9'}`}
            onClick={() => setIsHidden(true)}>
            {trigger}
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent className="w-full" hidden={isHidden}>
          <span>{hoverContent}</span>
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        className={cn('border-border shadow-xs w-full border p-2', theme === 'dark' ? 'dark' : '')}
        side={side}
        align={align}
        sideOffset={offset}
        onFocusOutside={(e) => {
          // The Chrome color picker's saturation grid and similar pickers
          // are non-focusable <div>s. Clicking them moves focus to <body>,
          // which Radix interprets as "focus moved outside" and dismisses.
          // We don't need focus-outside dismissal at all — pointer-down-
          // outside still fires for genuine outside clicks (canvas etc.)
          // and closes the popover correctly.
          e.preventDefault()
        }}>
        <div className="flex flex-col gap-2">
          {options.map((option, index) => (
            <div key={index} className="font-regular flex flex-col gap-2 text-sm">
              {option.label !== '' && (
                <div>{option.label.charAt(0).toUpperCase() + option.label.slice(1)}</div>
              )}
              {option.content}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
